import { applyPageMetadata } from "@/lib/page-metadata";
import { createFileRoute, Link, notFound, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { Layout, Section, Eyebrow, Breadcrumbs, FinalCTA, CTAButton } from "@/components/site/Layout";
import { caseStudies, getCaseStudy, type CaseStudy } from "@/data/case-studies";

export const Route = createFileRoute("/case-studies/$slug")({
  loader: ({ params }) => {
    const study = getCaseStudy(params.slug);
    if (!study) throw notFound();
    return { study };
  },
  head: ({ loaderData }) => {
    const s = loaderData?.study;
    if (!s) return {};
    return applyPageMetadata(`/case-studies/${s.slug}`, {
      meta: [
        { title: `${s.practice} SEO Case Study, ${s.market}` },
        { name: "description", content: `${s.shortHeadline} Full case study: strategy, timeline, and verified proof.` },
        { property: "og:title", content: `${s.practice} SEO Case Study, ${s.market}` },
        { property: "og:description", content: s.shortHeadline },
        { property: "og:type", content: "article" },
        { property: "og:image", content: s.cover },
        { property: "og:url", content: `https://pageonelawyers.com/case-studies/${s.slug}` },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: s.cover },
      ],
      links: [{ rel: "canonical", href: `https://pageonelawyers.com/case-studies/${s.slug}` }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: `${s.practice} SEO Case Study, ${s.market}`,
            description: s.shortHeadline,
            image: s.cover,
            author: { "@type": "Organization", name: "PageOne Lawyers" },
            publisher: { "@type": "Organization", name: "PageOne Lawyers" },
          }),
        },
      ],
    });
  },
  component: CaseStudyPage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <Layout>
        <Section>
          <h1 className="text-3xl font-semibold">Something went wrong</h1>
          <p className="mt-3 text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
          <button
            onClick={() => { reset(); router.invalidate(); }}
            className="mt-6 inline-flex items-center rounded-md bg-primary text-primary-foreground px-5 py-3 text-sm font-semibold"
          >
            Try again
          </button>
        </Section>
      </Layout>
    );
  },
  notFoundComponent: () => {
    const { slug } = Route.useParams();
    return (
      <Layout>
        <Section>
          <Eyebrow>Case study not found</Eyebrow>
          <h1 className="mt-4 font-display text-4xl font-semibold">No case study for "{slug}"</h1>
          <p className="mt-3 text-muted-foreground">Browse all case studies instead.</p>
          <Link to="/case-studies" className="mt-6 inline-block text-sm font-semibold text-primary hover:text-[var(--gold-deep)]">← All case studies</Link>
        </Section>
      </Layout>
    );
  },
});

function CaseStudyPage() {
  const { study: s } = Route.useLoaderData() as { study: CaseStudy };
  const related = caseStudies.filter((c) => c.slug !== s.slug).slice(0, 3);
  const [lightbox, setLightbox] = useState<{ src: string; alt: string; caption: string } | null>(null);

  return (
    <Layout>
      {/* HERO */}
      <section className="relative bg-hero-gradient text-primary-foreground overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src={s.cover} alt="" className="h-full w-full object-cover" aria-hidden />
          <div className="absolute inset-0 bg-gradient-to-r from-[var(--navy-deep)] via-[var(--navy-deep)]/85 to-transparent" />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 py-20">
          <Breadcrumbs items={[
            { label: "Home", href: "/" },
            { label: "Case Studies", href: "/case-studies" },
            { label: s.practice },
          ]} />
          <div className="flex flex-wrap gap-3 items-center mb-4">
            <span className="rounded-full bg-[var(--gold)] text-[var(--navy-deep)] px-3 py-1 text-xs font-bold">{s.practice}</span>
            <span className="text-sm text-primary-foreground/80">{s.market}</span>
            <span className="text-sm text-primary-foreground/60">• {s.timeline} engagement</span>
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-semibold max-w-4xl leading-[1.05]">{s.headline}</h1>
          <p className="mt-6 text-lg text-primary-foreground/80 max-w-3xl">{s.shortHeadline}</p>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
            {s.metrics.map(([m, l]) => (
              <div key={l} className="rounded-xl bg-white/[0.06] backdrop-blur border border-white/15 p-5 text-center">
                <div className="font-display text-3xl font-semibold text-[var(--gold)]">{m}</div>
                <div className="mt-1 text-xs text-primary-foreground/75">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COVER IMAGE */}
      <div className="border-b border-border">
        <div className="mx-auto max-w-6xl px-6 -mt-8 relative z-10">
          <img
            src={s.cover}
            alt={`${s.practice} case study, ${s.market}`}
            width={1400}
            height={900}
            className="w-full rounded-2xl shadow-2xl border border-border"
          />
        </div>
      </div>

      {/* CHALLENGE */}
      <Section>
        <div className="grid md:grid-cols-3 gap-12">
          <div>
            <Eyebrow>The Challenge</Eyebrow>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold">Where the firm started.</h2>
          </div>
          <div className="md:col-span-2">
            <p className="text-foreground leading-relaxed text-lg">{s.challenge}</p>
          </div>
        </div>
      </Section>

      {/* STRATEGY */}
      <div className="bg-secondary/50 border-y border-border">
        <Section>
          <div className="grid md:grid-cols-3 gap-12">
            <div>
              <Eyebrow>The Strategy</Eyebrow>
              <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold">What we shipped.</h2>
              <p className="mt-4 text-sm text-muted-foreground">Full-scope engagement, technical, content, local, and links working in parallel.</p>
            </div>
            <ul className="md:col-span-2 space-y-4">
              {s.strategy.map((step, i) => (
                <li key={i} className="flex gap-4 rounded-xl bg-card border border-border p-5">
                  <span className="flex-shrink-0 h-8 w-8 rounded-full bg-[var(--gold)] text-[var(--navy-deep)] flex items-center justify-center text-sm font-bold">{i + 1}</span>
                  <span className="text-foreground leading-relaxed">{step}</span>
                </li>
              ))}
            </ul>
          </div>
        </Section>
      </div>

      {/* RESULTS */}
      <Section>
        <div className="grid md:grid-cols-3 gap-12">
          <div>
            <Eyebrow>The Results</Eyebrow>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold">Where the firm is now.</h2>
          </div>
          <div className="md:col-span-2">
            <p className="text-foreground leading-relaxed text-lg">{s.results}</p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {s.metrics.map(([m, l]) => (
                <div key={l} className="rounded-xl bg-secondary/60 p-5 text-center">
                  <div className="font-display text-3xl font-semibold text-[var(--gold-deep)]">{m}</div>
                  <div className="mt-1 text-sm text-muted-foreground">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* PROOF IMAGES */}
      <div className="bg-[var(--navy-deep)] text-primary-foreground">
        <Section>
          <div className="max-w-3xl">
            <Eyebrow><span className="text-[var(--gold)]">Verified Proof</span></Eyebrow>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold">See the receipts.</h2>
            <p className="mt-4 text-primary-foreground/75">Analytics, SERP screenshots, and Google Business Profile data pulled directly from the client's accounts. Personally identifying details redacted per NDA.</p>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {s.proofImages.map((img, i) => (
              <figure key={i} className="group rounded-xl overflow-hidden bg-white/[0.04] border border-white/15 hover:border-[var(--gold)] transition-colors">
                <button
                  type="button"
                  onClick={() => setLightbox(img)}
                  className="block w-full aspect-[16/10] overflow-hidden bg-white cursor-zoom-in relative"
                  aria-label={`Enlarge proof image: ${img.alt}`}
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    width={1400}
                    height={900}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
                  />
                  <span className="absolute inset-0 flex items-center justify-center bg-[var(--navy-deep)]/0 group-hover:bg-[var(--navy-deep)]/40 transition-colors">
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity rounded-full bg-white/95 text-[var(--navy-deep)] text-xs font-semibold px-4 py-2 shadow-lg">
                      Click to enlarge
                    </span>
                  </span>
                </button>
                <figcaption className="p-5 text-sm text-primary-foreground/80 leading-relaxed">{img.caption}</figcaption>
              </figure>
            ))}
          </div>
        </Section>
      </div>

      {/* LIGHTBOX */}
      {lightbox && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={lightbox.alt}
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 md:p-8 animate-fade-in cursor-zoom-out"
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            aria-label="Close"
            className="absolute top-4 right-4 md:top-6 md:right-6 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 text-white text-2xl leading-none flex items-center justify-center transition"
          >
            ×
          </button>
          <figure
            onClick={(e) => e.stopPropagation()}
            className="relative max-w-6xl w-full max-h-full flex flex-col items-center animate-scale-in cursor-default"
          >
            <img
              src={lightbox.src}
              alt={lightbox.alt}
              className="w-full h-auto max-h-[80vh] object-contain rounded-lg shadow-2xl bg-white"
            />
            <figcaption className="mt-4 text-sm md:text-base text-white/85 text-center max-w-3xl leading-relaxed">
              {lightbox.caption}
            </figcaption>
          </figure>
        </div>
      )}

      {/* QUOTE */}
      <Section>
        <figure className="mx-auto max-w-3xl rounded-2xl border border-border bg-card p-8 md:p-12 shadow-[var(--shadow-card)] text-center">
          <div className="text-[var(--gold-deep)] text-5xl leading-none mb-4">"</div>
          <blockquote className="font-display text-2xl md:text-3xl font-medium text-foreground leading-snug">{s.quote.text}</blockquote>
          <figcaption className="mt-6 text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">{s.quote.author}</span>, {s.quote.role}
          </figcaption>
        </figure>
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <CTAButton to="/law-firm-seo-audit" variant="gold">Get my free SEO audit</CTAButton>
          <CTAButton to={s.practiceHref} variant="primary">Explore {s.practice} SEO →</CTAButton>
        </div>
      </Section>

      {/* RELATED */}
      <div className="bg-secondary/40 border-y border-border">
        <Section>
          <Eyebrow>Other case studies</Eyebrow>
          <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold">More firms we've grown.</h2>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {related.map((r) => (
              <Link
                key={r.slug}
                to="/case-studies/$slug"
                params={{ slug: r.slug }}
                className="group rounded-xl overflow-hidden border border-border bg-card hover:border-[var(--gold)] transition"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={r.cover} alt={r.practice} width={1400} height={900} loading="lazy" className="h-full w-full object-cover group-hover:scale-[1.03] transition duration-500" />
                </div>
                <div className="p-5">
                  <div className="text-xs font-semibold text-[var(--gold-deep)] uppercase tracking-wider">{r.practice} • {r.market}</div>
                  <h3 className="mt-2 font-semibold text-foreground line-clamp-2">{r.headline}</h3>
                </div>
              </Link>
            ))}
          </div>
        </Section>
      </div>

      <FinalCTA title="Ready to be our next case study?" sub="Every engagement starts with a free audit. We'll tell you what's realistically possible in your market, with numbers, not vibes." />
    </Layout>
  );
}